// src/components/EnhancedRegistration.js
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import logger from '../utils/logger';
import { 
  ArrowRight, 
  Check, 
  ChevronRight, 
  Mail, 
  Building, 
  Globe, 
  Server, 
  Database, 
  Users, 
  Phone, 
  Calendar, 
  CreditCard,
  CheckCircle,
  Lock,
  AlertCircle,
  X
} from 'lucide-react';

// Keep your original component largely intact, but modify the form submission
const EnhancedRegistration = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [error, setError] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    // Step 1: Company Information
    companyName: '',
    companyEmail: '',
    companyWebsite: '',
    companyPhone: '',
    industry: '',
    
    // Step 2: Business Central Details
    tenantId: '',
    domainName: '',
    bcEnvironment: '',
    bcCompanyName: '',
    numberOfEmployees: '',
    
    // Step 3: Plan Selection
    selectedPlan: 'essential',
    paymentFrequency: 'annual',
    
    // Step 4: Contact & Confirmation
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    agreeTerms: false,
    agreeDataProcessing: false
  });
  
  // Industry options
  const industries = [
    'Accounting & Finance',
    'Construction',
    'Consulting',
    'Education',
    'Healthcare',
    'Information Technology',
    'Manufacturing',
    'Retail',
    'Transportation',
    'Other'
  ];
  
  // Handle form field changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };
  
  // Handle plan selection
  const handlePlanSelect = (plan) => {
    setFormData({
      ...formData,
      selectedPlan: plan
    });
  };
  
  // Handle payment frequency selection
  const handleFrequencySelect = (frequency) => {
    setFormData({
      ...formData,
      paymentFrequency: frequency
    });
  };
  
  // Handle step navigation
  const nextStep = () => {
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
      window.scrollTo(0, 0);
    }
  };
  
  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      window.scrollTo(0, 0);
    }
  };

  // Dismisses the error message
  const dismissError = () => {
    setError(null);
  };
  
  // Handle form submission - UPDATED for BC integration and improved error handling
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    
    try {
      // Prepare registration data from form
      const registrationData = {
        "company_Name": formData.companyName,
        "contactEmail": formData.contactEmail || formData.companyEmail,
        "contactName": formData.contactName,
        "tenantId": formData.tenantId,
        "primaryEnvironment": formData.bcEnvironment,
        "primaryCompanyName": formData.bcCompanyName,
        "numberOfEmployees": parseInt(formData.numberOfEmployees, 10) || 1,
        "industry": formData.industry,
        "registrationSource": "Portal",
        "vatRegistrationNo": "",
        "companyRegistrationNo": "",
        "primaryContactLanguage": "EN",
        "address": formData.address || "",
        "city": formData.city || "",
        "county": formData.county || "",
        "postCode": formData.postCode || "",
        "countryRegionCode": formData.countryRegionCode || ""
      };
  
      logger.log('Sending registration data:', JSON.stringify(registrationData, null, 2));
      
      // Use fetch directly for better debugging      
      const apiUrl = process.env.REACT_APP_API_BASE_URL;      
      
      logger.log('Sending to:', apiUrl);
            
      const response = await fetch(`${apiUrl}/bc/portalRegistrations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(registrationData)
      });
  
      // Log the complete response
      const responseText = await response.text();
      logger.log('Registration response status:', response.status);
      logger.log('Registration response headers:', response.headers);
      logger.log('Registration response text:', responseText);
      
      let result;
      try {
        result = JSON.parse(responseText);
      } catch (e) {
        result = { message: responseText };
      }
      
      if (!response.ok) {
        // Directly use the BC error format if available, otherwise use a generic message
        if (result && typeof result === 'object') {
          if (result.error) {
            throw result.error;
          } else if (result.message) {
            throw result.message;
          } else {
            throw JSON.stringify(result);  // Convert object to string to show exact content
          }
        } else {
          throw new Error(`API Error: ${response.status}`);
        }
      }
      
      logger.log('Registration successful:', result);
      setFormSubmitted(true);
      setIsLoading(false);
      
    } catch (error) {
      logger.error('Registration error:', error);
      
      // Improved error handling to extract the message from Business Central
      let errorMessage = 'An error occurred during registration. Please try again.';
      
      // Check if error is already a string
      if (typeof error === 'string') {
        errorMessage = error;
      } 
      // Check if it's an Error object
      else if (error instanceof Error) {
        errorMessage = error.message;
      }
      // Check if it's a BC API error object
      else if (typeof error === 'object' && error !== null) {
        // Extract the most specific error message possible
        if (error.message) {
          errorMessage = error.message;
        } else if (error.error && typeof error.error === 'string') {
          errorMessage = error.error;
        } else if (error.error && error.error.message) {
          errorMessage = error.error.message;
        } else {
          // If all else fails, stringify the entire object to show the exact structure
          try {
            errorMessage = JSON.stringify(error);
          } catch (e) {
            errorMessage = 'Unknown error format received from server';
          }
        }
      }
      
      // Clean up the error message
      if (typeof errorMessage === 'string' && errorMessage.includes('CorrelationId:')) {
        // Remove the correlation ID from the message
        errorMessage = errorMessage.split('CorrelationId:')[0].trim();
      }
      
      setError(errorMessage);
      setIsLoading(false);
    }
  };
  
  // Get step validation status
  const isStepValid = (step) => {
    switch(step) {
      case 1:
        return formData.companyName && formData.companyEmail;
      case 2:
        return formData.tenantId && formData.domainName && 
               formData.bcEnvironment && 
               formData.bcCompanyName && 
               formData.numberOfEmployees;
      case 3:
        return formData.selectedPlan && formData.paymentFrequency;
      case 4:
        return formData.contactName && 
               formData.contactEmail && 
               formData.agreeTerms && 
               formData.agreeDataProcessing;
      default:
        return false;
    }
  };
  
  // Determine pricing based on plan and frequency
  const getPricing = () => {
    const basePricing = {
      basic: 0.5,
      essential: 5,
      premium: 7
    };
    
    // 20% discount for annual billing
    const price = basePricing[formData.selectedPlan];
    return {
      price,
      monthlyPrice: formData.paymentFrequency === 'annual' ? (price * 0.8).toFixed(2) : price,
      annualPrice: formData.paymentFrequency === 'annual' ? (price * 0.8 * 12).toFixed(2) : (price * 12).toFixed(2),
      discount: formData.paymentFrequency === 'annual' ? '20%' : '0%'
    };
  };
  
  // Error Alert Component
  const ErrorAlert = ({ message, onDismiss }) => {
    if (!message) return null;
    
    return (
      <div className="rounded-md bg-red-50 p-4 mb-6 border border-red-200 shadow-sm">
        <div className="flex justify-between">
          <div className="flex">
            <div className="flex-shrink-0">
              <AlertCircle className="h-5 w-5 text-red-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Registration Error</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{message}</p>
              </div>
            </div>
          </div>
          <button
            type="button"
            className="inline-flex text-red-400 hover:text-red-500 focus:outline-none"
            onClick={onDismiss}
          >
            <span className="sr-only">Dismiss</span>
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>
    );
  };
  
  // KEEP THE REST OF YOUR COMPONENT AS IS
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 items-center">
            <div className="flex items-center">
              <Link to="/" className="flex-shrink-0 flex items-center">
                <svg className="h-8 w-8 text-indigo-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 5v14M5 12h14"/>
                </svg>
                <span className="ml-2 text-xl font-semibold tracking-tight text-gray-900">Business Central</span>
              </Link>
            </div>
            <div>
              <button onClick={() => navigate('/')} className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
                Back to home
              </button>
            </div>
          </div>
        </div>
      </header>
      
      <div className="flex-1 py-12">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Title and Progress */}
          <div className="text-center mb-12">
            <h1 className="text-3xl font-extrabold text-gray-900">Register your Business Central</h1>
            <p className="mt-4 text-lg text-gray-500">
              Connect your Business Central environment to provide employees with secure access to their data
            </p>
          </div>
          
          {formSubmitted ? (
            <div className="bg-white rounded-lg shadow-lg p-8 text-center">
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-6">
                <CheckCircle className="h-10 w-10 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Registration Successful!</h2>
              <p className="text-lg text-gray-600 mb-6">
                Thank you for registering your Business Central environment. We've sent a confirmation email to {formData.contactEmail || formData.companyEmail} with further instructions.
              </p>
              <div className="mt-8 flex justify-center">
                <button
                  onClick={() => navigate('/')}
                  className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
                >
                  Return to Home
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* Progress Steps */}
              <div className="mb-12">
                <nav aria-label="Progress">
                  <ol className="flex items-center">
                    <li className={`relative pr-8 sm:pr-20 ${currentStep >= 1 ? 'text-indigo-600' : 'text-gray-400'}`}>
                      <div className="absolute inset-0 flex items-center" aria-hidden="true">
                        <div className={`h-0.5 w-full ${currentStep > 1 ? 'bg-indigo-600' : 'bg-gray-200'}`}></div>
                      </div>
                      <div className={`relative flex h-8 w-8 items-center justify-center rounded-full ${
                        currentStep > 1 ? 'bg-indigo-600' : currentStep === 1 ? 'border-2 border-indigo-600 bg-white' : 'border-2 border-gray-300 bg-white'
                      }`}>
                        {currentStep > 1 ? (
                          <Check className={`h-5 w-5 text-white`} />
                        ) : (
                          <span className={`${currentStep === 1 ? 'text-indigo-600' : 'text-gray-500'}`}>1</span>
                        )}
                      </div>
                      <div className="absolute top-10 text-center w-32 -left-12 text-sm font-medium">
                        Company Information
                      </div>
                    </li>
                    
                    <li className={`relative pr-8 sm:pr-20 ${currentStep >= 2 ? 'text-indigo-600' : 'text-gray-400'}`}>
                      <div className="absolute inset-0 flex items-center" aria-hidden="true">
                        <div className={`h-0.5 w-full ${currentStep > 2 ? 'bg-indigo-600' : 'bg-gray-200'}`}></div>
                      </div>
                      <div className={`relative flex h-8 w-8 items-center justify-center rounded-full ${
                        currentStep > 2 ? 'bg-indigo-600' : currentStep === 2 ? 'border-2 border-indigo-600 bg-white' : 'border-2 border-gray-300 bg-white'
                      }`}>
                        {currentStep > 2 ? (
                          <Check className={`h-5 w-5 text-white`} />
                        ) : (
                          <span className={`${currentStep === 2 ? 'text-indigo-600' : 'text-gray-500'}`}>2</span>
                        )}
                      </div>
                      <div className="absolute top-10 text-center w-32 -left-12 text-sm font-medium">
                        BC Configuration
                      </div>
                    </li>
                    
                    <li className={`relative pr-8 sm:pr-20 ${currentStep >= 3 ? 'text-indigo-600' : 'text-gray-400'}`}>
                      <div className="absolute inset-0 flex items-center" aria-hidden="true">
                        <div className={`h-0.5 w-full ${currentStep > 3 ? 'bg-indigo-600' : 'bg-gray-200'}`}></div>
                      </div>
                      <div className={`relative flex h-8 w-8 items-center justify-center rounded-full ${
                        currentStep > 3 ? 'bg-indigo-600' : currentStep === 3 ? 'border-2 border-indigo-600 bg-white' : 'border-2 border-gray-300 bg-white'
                      }`}>
                        {currentStep > 3 ? (
                          <Check className={`h-5 w-5 text-white`} />
                        ) : (
                          <span className={`${currentStep === 3 ? 'text-indigo-600' : 'text-gray-500'}`}>3</span>
                        )}
                      </div>
                      <div className="absolute top-10 text-center w-32 -left-12 text-sm font-medium">
                        Select Plan
                      </div>
                    </li>
                    
                    <li className={`relative ${currentStep >= 4 ? 'text-indigo-600' : 'text-gray-400'}`}>
                      <div className={`relative flex h-8 w-8 items-center justify-center rounded-full ${
                        currentStep > 4 ? 'bg-indigo-600' : currentStep === 4 ? 'border-2 border-indigo-600 bg-white' : 'border-2 border-gray-300 bg-white'
                      }`}>
                        {currentStep > 4 ? (
                          <Check className={`h-5 w-5 text-white`} />
                        ) : (
                          <span className={`${currentStep === 4 ? 'text-indigo-600' : 'text-gray-500'}`}>4</span>
                        )}
                      </div>
                      <div className="absolute top-10 text-center w-32 -left-12 text-sm font-medium">
                        Confirmation
                      </div>
                    </li>
                  </ol>
                </nav>
              </div>
              
              <div className="bg-white shadow-lg rounded-lg border border-gray-200 overflow-hidden">
                {/* Display any errors at the top level of the form */}
                {error && (
                  <div className="px-6 py-4">
                    <ErrorAlert message={error} onDismiss={dismissError} />
                  </div>
                )}
                
                <form onSubmit={handleSubmit}>
                  {/* Step 1: Company Information */}
                  {currentStep === 1 && (
                    <div>
                      <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                        <h2 className="text-lg font-medium text-gray-900">Company Information</h2>
                        <p className="mt-1 text-sm text-gray-500">
                          Tell us about your organization
                        </p>
                      </div>
                      <div className="px-6 py-6 space-y-6">
                        <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2">
                          <div className="sm:col-span-2">
                            <label htmlFor="companyName" className="block text-sm font-medium text-gray-700">
                              Company Name <span className="text-red-500">*</span>
                            </label>
                            <div className="mt-1 relative rounded-md shadow-sm">
                              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <Building className="h-5 w-5 text-gray-400" />
                              </div>
                              <input
                                type="text"
                                id="companyName"
                                name="companyName"
                                value={formData.companyName}
                                onChange={handleChange}
                                className="block w-full pl-10 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                placeholder="Acme Corporation Ltd"
                                required
                              />
                            </div>
                          </div>
                          
                          <div className="sm:col-span-2">
                            <label htmlFor="companyEmail" className="block text-sm font-medium text-gray-700">
                              Company Email <span className="text-red-500">*</span>
                            </label>
                            <div className="mt-1 relative rounded-md shadow-sm">
                              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <Mail className="h-5 w-5 text-gray-400" />
                              </div>
                              <input
                                type="email"
                                id="companyEmail"
                                name="companyEmail"
                                value={formData.companyEmail}
                                onChange={handleChange}
                                className="block w-full pl-10 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                placeholder="info@company.com"
                                required
                              />
                            </div>
                            <p className="mt-2 text-sm text-gray-500">We'll send important account notifications to this email</p>
                          </div>
                          
                          <div>
                            <label htmlFor="companyWebsite" className="block text-sm font-medium text-gray-700">
                              Company Website
                            </label>
                            <div className="mt-1 relative rounded-md shadow-sm">
                              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <Globe className="h-5 w-5 text-gray-400" />
                              </div>
                              <input
                                type="url"
                                id="companyWebsite"
                                name="companyWebsite"
                                value={formData.companyWebsite}
                                onChange={handleChange}
                                className="block w-full pl-10 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                placeholder="https://company.com"
                              />
                            </div>
                          </div>
                          
                          <div>
                            <label htmlFor="companyPhone" className="block text-sm font-medium text-gray-700">
                              Company Phone
                            </label>
                            <div className="mt-1 relative rounded-md shadow-sm">
                              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <Phone className="h-5 w-5 text-gray-400" />
                              </div>
                              <input
                                type="tel"
                                id="companyPhone"
                                name="companyPhone"
                                value={formData.companyPhone}
                                onChange={handleChange}
                                className="block w-full pl-10 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                placeholder="+1 (123) 456-7890"
                              />
                            </div>
                          </div>
                          
                          <div className="sm:col-span-2">
                            <label htmlFor="industry" className="block text-sm font-medium text-gray-700">
                              Industry
                            </label>
                            <div className="mt-1">
                              <select
                                id="industry"
                                name="industry"
                                value={formData.industry}
                                onChange={handleChange}
                                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                              >
                                <option value="">Select an industry</option>
                                {industries.map(industry => (
                                  <option key={industry} value={industry}>{industry}</option>
                                ))}
                              </select>
                            </div>
                          </div>
                        </div>
                        
                        <div className="pt-4">
                          <div className="flex justify-end">
                            <button
                              type="button"
                              onClick={nextStep}
                              disabled={!isStepValid(1)}
                              className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white ${
                                isStepValid(1) ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-indigo-300 cursor-not-allowed'
                              }`}
                            >
                              Continue
                              <ChevronRight className="ml-2 h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Step 2: Business Central Configuration */}
                  {currentStep === 2 && (
                    <div>
                      <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                        <h2 className="text-lg font-medium text-gray-900">Business Central Configuration</h2>
                        <p className="mt-1 text-sm text-gray-500">
                          Connect your Business Central environment
                        </p>
                      </div>
                      <div className="px-6 py-6 space-y-6">
                        <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2">
                          <div className="sm:col-span-1">
                            <label htmlFor="tenantId" className="block text-sm font-medium text-gray-700">
                              Tenant ID <span className="text-red-500">*</span>
                            </label>
                            <div className="mt-1 relative rounded-md shadow-sm">
                              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <Database className="h-5 w-5 text-gray-400" />
                              </div>
                              <input
                                type="text"
                                id="tenantId"
                                name="tenantId"
                                value={formData.tenantId}
                                onChange={handleChange}
                                className="block w-full pl-10 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                                required
                              />
                            </div>
                          </div>
                          
                          <div className="sm:col-span-1">
                            <label htmlFor="domainName" className="block text-sm font-medium text-gray-700">
                              Domain Name <span className="text-red-500">*</span>
                            </label>
                            <div className="mt-1 relative rounded-md shadow-sm">
                              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <Globe className="h-5 w-5 text-gray-400" />
                              </div>
                              <input
                                type="text"
                                id="domainName"
                                name="domainName"
                                value={formData.domainName}
                                onChange={handleChange}
                                className="block w-full pl-10 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                placeholder="company.onmicrosoft.com"
                                required
                              />
                            </div>
                          </div>
                          
                          <div className="sm:col-span-2">
                            <label htmlFor="bcEnvironment" className="block text-sm font-medium text-gray-700">
                              Business Central Environment <span className="text-red-500">*</span>
                            </label>
                            <div className="mt-1 relative rounded-md shadow-sm">
                              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <Server className="h-5 w-5 text-gray-400" />
                              </div>
                              <input
                                type="text"
                                id="bcEnvironment"
                                name="bcEnvironment"
                                value={formData.bcEnvironment}
                                onChange={handleChange}
                                className="block w-full pl-10 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                placeholder="Production"
                                required
                              />
                            </div>
                          </div>
                          
                          <div className="sm:col-span-2">
                            <label htmlFor="bcCompanyName" className="block text-sm font-medium text-gray-700">
                              Business Central Company Name <span className="text-red-500">*</span>
                            </label>
                            <div className="mt-1 relative rounded-md shadow-sm">
                              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <Building className="h-5 w-5 text-gray-400" />
                              </div>
                              <input
                                type="text"
                                id="bcCompanyName"
                                name="bcCompanyName"
                                value={formData.bcCompanyName}
                                onChange={handleChange}
                                className="block w-full pl-10 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                placeholder="CRONUS UK Ltd."
                                required
                              />
                            </div>
                          </div>
                          
                          <div className="sm:col-span-2">
                            <label htmlFor="numberOfEmployees" className="block text-sm font-medium text-gray-700">
                              Number of Employees <span className="text-red-500">*</span>
                            </label>
                            <div className="mt-1 relative rounded-md shadow-sm">
                              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <Users className="h-5 w-5 text-gray-400" />
                              </div>
                              <input
                                type="number"
                                id="numberOfEmployees"
                                name="numberOfEmployees"
                                value={formData.numberOfEmployees}
                                onChange={handleChange}
                                min="1"
                                className="block w-full pl-10 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                placeholder="25"
                                required
                              />
                            </div>
                            <p className="mt-2 text-sm text-gray-500">
                              This is for approximate calculation purposes only. The actual number of portal users will be determined by the number of employees with salaries posted per month.
                            </p>
                          </div>
                        </div>
                        
                        <div className="pt-4">
                          <div className="flex justify-between">
                            <button
                              type="button"
                              onClick={prevStep}
                              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                            >
                              Back
                            </button>
                            <button
                              type="button"
                              onClick={nextStep}
                              disabled={!isStepValid(2)}
                              className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white ${
                                isStepValid(2) ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-indigo-300 cursor-not-allowed'
                              }`}
                            >
                              Continue
                              <ChevronRight className="ml-2 h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Step 3: Plan Selection */}
                  {currentStep === 3 && (
                    <div>
                      <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                        <h2 className="text-lg font-medium text-gray-900">Select Your Plan</h2>
                        <p className="mt-1 text-sm text-gray-500">
                          Choose the right plan for your organization
                        </p>
                      </div>
                      <div className="px-6 py-6 space-y-8">
                        {/* Payment frequency */}
                        <div>
                          <label className="text-sm font-medium text-gray-700">Billing Frequency</label>
                          <div className="mt-2 grid grid-cols-2 gap-3">
                            <div 
                              className={`flex items-center justify-center rounded-md border ${formData.paymentFrequency === 'monthly' 
                                ? 'border-indigo-500 bg-indigo-50' 
                                : 'border-gray-300 bg-white'} py-3 px-4 text-sm font-medium cursor-pointer`}
                              onClick={() => handleFrequencySelect('monthly')}
                            >
                              <Calendar className={`mr-2 h-5 w-5 ${formData.paymentFrequency === 'monthly' ? 'text-indigo-600' : 'text-gray-400'}`} />
                              <span className={formData.paymentFrequency === 'monthly' ? 'text-indigo-900' : 'text-gray-900'}>Monthly</span>
                            </div>
                            
                            <div 
                              className={`flex items-center justify-center rounded-md border ${formData.paymentFrequency === 'annual' 
                                ? 'border-indigo-500 bg-indigo-50' 
                                : 'border-gray-300 bg-white'} py-3 px-4 text-sm font-medium cursor-pointer`}
                              onClick={() => handleFrequencySelect('annual')}
                            >
                              <Calendar className={`mr-2 h-5 w-5 ${formData.paymentFrequency === 'annual' ? 'text-indigo-600' : 'text-gray-400'}`} />
                              <span className={formData.paymentFrequency === 'annual' ? 'text-indigo-900' : 'text-gray-900'}>Annual (Save 20%)</span>
                            </div>
                          </div>
                        </div>
                        
                        {/* Plan Options */}
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                          {/* Basic Plan */}
                          <div 
                            className={`border rounded-lg overflow-hidden cursor-pointer ${
                              formData.selectedPlan === 'basic' ? 'border-indigo-500 ring-2 ring-indigo-200' : 'border-gray-200 hover:border-gray-300'
                            }`}
                            onClick={() => handlePlanSelect('basic')}
                          >
                            <div className="p-5">
                              <h3 className="text-lg font-medium text-gray-900">Basic</h3>
                              <div className="mt-2 flex items-baseline">
                                <span className="text-2xl font-semibold text-gray-900">£0.5</span>
                                <span className="ml-1 text-sm text-gray-500">/ user / month</span>
                              </div>
                              <p className="mt-4 text-sm text-gray-500">Essential employee self-service for payslip access</p>
                              <ul className="mt-6 space-y-4">
                                <li className="flex items-start">
                                  <Check className="h-5 w-5 flex-shrink-0 text-green-500" />
                                  <span className="ml-2 text-sm text-gray-700">Profile page visibility</span>
                                </li>
                                <li className="flex items-start">
                                  <Check className="h-5 w-5 flex-shrink-0 text-green-500" />
                                  <span className="ml-2 text-sm text-gray-700">Payslip access</span>
                                </li>
                                <li className="flex items-start">
                                  <Check className="h-5 w-5 flex-shrink-0 text-green-500" />
                                  <span className="ml-2 text-sm text-gray-700">Secure authentication</span>
                                </li>
                              </ul>
                            </div>
                            {formData.selectedPlan === 'basic' && (
                              <div className="bg-indigo-100 p-3 text-center">
                                <span className="text-sm font-medium text-indigo-800">Selected</span>
                              </div>
                            )}
                          </div>
                          
                          {/* Essential Plan */}
                          <div 
                            className={`border rounded-lg overflow-hidden cursor-pointer ${
                              formData.selectedPlan === 'essential' ? 'border-indigo-500 ring-2 ring-indigo-200' : 'border-gray-200 hover:border-gray-300'
                            }`}
                            onClick={() => handlePlanSelect('essential')}
                          >
                            <div className="p-5 relative">
                              <div className="absolute top-0 right-0 bg-indigo-600 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
                                POPULAR
                              </div>
                              <h3 className="text-lg font-medium text-gray-900">Essential</h3>
                              <div className="mt-2 flex items-baseline">
                                <span className="text-2xl font-semibold text-gray-900">£5</span>
                                <span className="ml-1 text-sm text-gray-500">/ user / month</span>
                              </div>
                              <p className="mt-4 text-sm text-gray-500">Complete employee portal with expanded features</p>
                              <ul className="mt-6 space-y-4">
                                <li className="flex items-start">
                                  <Check className="h-5 w-5 flex-shrink-0 text-green-500" />
                                  <span className="ml-2 text-sm text-gray-700">All Basic features</span>
                                </li>
                                <li className="flex items-start">
                                  <Check className="h-5 w-5 flex-shrink-0 text-green-500" />
                                  <span className="ml-2 text-sm text-gray-700">Document management</span>
                                </li>
                                <li className="flex items-start">
                                  <Check className="h-5 w-5 flex-shrink-0 text-green-500" />
                                  <span className="ml-2 text-sm text-gray-700">Leave management</span>
                                </li>
                              </ul>
                            </div>
                            {formData.selectedPlan === 'essential' && (
                              <div className="bg-indigo-100 p-3 text-center">
                                <span className="text-sm font-medium text-indigo-800">Selected</span>
                              </div>
                            )}
                          </div>
                          
                          {/* Premium Plan */}
                          <div 
                            className={`border rounded-lg overflow-hidden cursor-pointer ${
                              formData.selectedPlan === 'premium' ? 'border-indigo-500 ring-2 ring-indigo-200' : 'border-gray-200 hover:border-gray-300'
                            }`}
                            onClick={() => handlePlanSelect('premium')}
                          >
                            <div className="p-5">
                              <h3 className="text-lg font-medium text-gray-900">Premium</h3>
                              <div className="mt-2 flex items-baseline">
                                <span className="text-2xl font-semibold text-gray-900">£7</span>
                                <span className="ml-1 text-sm text-gray-500">/ user / month</span>
                              </div>
                              <p className="mt-4 text-sm text-gray-500">Full-featured solution with advanced capabilities</p>
                              <ul className="mt-6 space-y-4">
                                <li className="flex items-start">
                                  <Check className="h-5 w-5 flex-shrink-0 text-green-500" />
                                  <span className="ml-2 text-sm text-gray-700">All Essential features</span>
                                </li>
                                <li className="flex items-start">
                                  <Check className="h-5 w-5 flex-shrink-0 text-green-500" />
                                  <span className="ml-2 text-sm text-gray-700">Starter checklist</span>
                                </li>
                                <li className="flex items-start">
                                  <Check className="h-5 w-5 flex-shrink-0 text-green-500" />
                                  <span className="ml-2 text-sm text-gray-700">Priority support</span>
                                </li>
                              </ul>
                            </div>
                            {formData.selectedPlan === 'premium' && (
                              <div className="bg-indigo-100 p-3 text-center">
                                <span className="text-sm font-medium text-indigo-800">Selected</span>
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {/* Order Summary */}
                        <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                          <h3 className="text-lg font-medium text-gray-900">Order Summary</h3>
                          <div className="mt-6 flex justify-between">
                            <div>
                              <p className="text-sm text-gray-500">
                                {formData.selectedPlan.charAt(0).toUpperCase() + formData.selectedPlan.slice(1)} Plan 
                                ({formData.numberOfEmployees} {parseInt(formData.numberOfEmployees) === 1 ? 'user' : 'users'})
                              </p>
                              <p className="text-sm text-gray-500 mt-1">
                                Billed {formData.paymentFrequency === 'annual' ? 'annually' : 'monthly'}
                                {formData.paymentFrequency === 'annual' && <span className="text-green-600 ml-2">(20% discount applied)</span>}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-lg font-semibold text-gray-900">
                                £{formData.paymentFrequency === 'annual' 
                                  ? (getPricing().annualPrice * formData.numberOfEmployees).toFixed(2)
                                  : (getPricing().monthlyPrice * formData.numberOfEmployees).toFixed(2)
                                }
                              </p>
                              <p className="text-sm text-gray-500 mt-1">
                                {formData.paymentFrequency === 'annual' ? 'per year' : 'per month'}
                              </p>
                            </div>
                          </div>
                          
                          <div className="mt-6 pt-6 border-t border-gray-200">
                            <div className="flex justify-between items-center">
                              <p className="text-sm font-medium text-gray-900">Total per user</p>
                              <p className="text-sm font-medium text-gray-900">
                                £{getPricing().monthlyPrice} / month
                              </p>
                            </div>
                          </div>
                        </div>
                        
                        <div className="pt-4">
                          <div className="flex justify-between">
                            <button
                              type="button"
                              onClick={prevStep}
                              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                            >
                              Back
                            </button>
                            <button
                              type="button"
                              onClick={nextStep}
                              disabled={!isStepValid(3)}
                              className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white ${
                                isStepValid(3) ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-indigo-300 cursor-not-allowed'
                              }`}
                            >
                              Continue
                              <ChevronRight className="ml-2 h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Step 4: Confirmation */}
                  {currentStep === 4 && (
                    <div>
                      <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                        <h2 className="text-lg font-medium text-gray-900">Contact & Confirmation</h2>
                        <p className="mt-1 text-sm text-gray-500">
                          Confirm your details and complete registration
                        </p>
                      </div>
                      <div className="px-6 py-6 space-y-6">
                        <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2">
                          <div className="sm:col-span-2">
                            <label htmlFor="contactName" className="block text-sm font-medium text-gray-700">
                              Contact Name <span className="text-red-500">*</span>
                            </label>
                            <div className="mt-1">
                              <input
                                type="text"
                                id="contactName"
                                name="contactName"
                                value={formData.contactName}
                                onChange={handleChange}
                                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                placeholder="John Smith"
                                required
                              />
                            </div>
                          </div>
                          
                          <div>
                            <label htmlFor="contactEmail" className="block text-sm font-medium text-gray-700">
                              Contact Email <span className="text-red-500">*</span>
                            </label>
                            <div className="mt-1">
                              <input
                                type="email"
                                id="contactEmail"
                                name="contactEmail"
                                value={formData.contactEmail}
                                onChange={handleChange}
                                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                placeholder="john.smith@company.com"
                                required
                              />
                            </div>
                          </div>
                          
                          <div>
                            <label htmlFor="contactPhone" className="block text-sm font-medium text-gray-700">
                              Contact Phone
                            </label>
                            <div className="mt-1">
                              <input
                                type="tel"
                                id="contactPhone"
                                name="contactPhone"
                                value={formData.contactPhone}
                                onChange={handleChange}
                                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                placeholder="+1 (123) 456-7890"
                              />
                            </div>
                          </div>
                        </div>
                        
                        {/* Summary */}
                        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                          <h3 className="text-sm font-medium text-gray-900 uppercase tracking-wider mb-3">Registration Summary</h3>
                          <dl className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2">
                            <div className="sm:col-span-1">
                              <dt className="text-sm font-medium text-gray-500">Company Name</dt>
                              <dd className="mt-1 text-sm text-gray-900">{formData.companyName}</dd>
                            </div>
                            <div className="sm:col-span-1">
                              <dt className="text-sm font-medium text-gray-500">Company Email</dt>
                              <dd className="mt-1 text-sm text-gray-900">{formData.companyEmail}</dd>
                            </div>
                            <div className="sm:col-span-1">
                              <dt className="text-sm font-medium text-gray-500">Business Central Environment</dt>
                              <dd className="mt-1 text-sm text-gray-900">{formData.bcEnvironment}</dd>
                            </div>
                            <div className="sm:col-span-1">
                              <dt className="text-sm font-medium text-gray-500">Business Central Company</dt>
                              <dd className="mt-1 text-sm text-gray-900">{formData.bcCompanyName}</dd>
                            </div>
                            <div className="sm:col-span-1">
                              <dt className="text-sm font-medium text-gray-500">Selected Plan</dt>
                              <dd className="mt-1 text-sm text-gray-900">
                                {formData.selectedPlan.charAt(0).toUpperCase() + formData.selectedPlan.slice(1)} (£{getPricing().monthlyPrice}/user/month)
                              </dd>
                            </div>
                            <div className="sm:col-span-1">
                              <dt className="text-sm font-medium text-gray-500">Number of Employees</dt>
                              <dd className="mt-1 text-sm text-gray-900">{formData.numberOfEmployees}</dd>
                            </div>
                          </dl>
                        </div>
                        
                        {/* Secure information notice */}
                        <div className="rounded-md bg-blue-50 p-4">
                          <div className="flex">
                            <div className="flex-shrink-0">
                              <Lock className="h-5 w-5 text-blue-400" />
                            </div>
                            <div className="ml-3">
                              <p className="text-sm text-blue-700">
                                After registration, you will receive an email with instructions to complete the setup. Your information is secure and will only be used for the purpose of providing the Business Central Employee Portal service.
                              </p>
                            </div>
                          </div>
                        </div>
                        
                        {/* Terms and conditions for employee count */}
                        <div className="mt-4 rounded-md bg-gray-50 p-4 border border-gray-200">
                          <h4 className="text-sm font-medium text-gray-900 mb-2">Important Terms & Conditions</h4>
                          <ul className="space-y-2 text-xs text-gray-600">
                            <li className="flex items-start">
                              <div className="flex-shrink-0 mt-0.5">
                                <svg className="h-3.5 w-3.5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                              </div>
                              <p className="ml-2">
                                <span className="font-medium">Employee Count:</span> The number provided during registration is for approximate calculation purposes only. Actual billing will be based on the number of employees with salaries posted in the system each month.
                              </p>
                            </li>
                            <li className="flex items-start">
                              <div className="flex-shrink-0 mt-0.5">
                                <svg className="h-3.5 w-3.5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                              </div>
                              <p className="ml-2">
                                <span className="font-medium">Billing Adjustments:</span> Monthly invoices will automatically adjust based on the actual number of active employees in the system.
                              </p>
                            </li>
                            <li className="flex items-start">
                              <div className="flex-shrink-0 mt-0.5">
                                <svg className="h-3.5 w-3.5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                              </div>
                              <p className="ml-2">
                                <span className="font-medium">Minimum Fee:</span> A minimum monthly fee equivalent to 5 users applies to all accounts regardless of actual usage.
                              </p>
                            </li>
                          </ul>
                        </div>
                        
                        {/* Terms and conditions */}
                        <div className="space-y-4">
                          <div className="flex items-start">
                            <div className="flex items-center h-5">
                              <input
                                id="agreeTerms"
                                name="agreeTerms"
                                type="checkbox"
                                checked={formData.agreeTerms}
                                onChange={handleChange}
                                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                                required
                              />
                            </div>
                            <div className="ml-3 text-sm">
                              <label htmlFor="agreeTerms" className="font-medium text-gray-700">
                                I agree to the <a href="#" className="text-indigo-600 hover:text-indigo-500">Terms of Service</a> and <a href="#" className="text-indigo-600 hover:text-indigo-500">Privacy Policy</a> <span className="text-red-500">*</span>
                              </label>
                            </div>
                          </div>
                          
                          <div className="flex items-start">
                            <div className="flex items-center h-5">
                              <input
                                id="agreeDataProcessing"
                                name="agreeDataProcessing"
                                type="checkbox"
                                checked={formData.agreeDataProcessing}
                                onChange={handleChange}
                                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                                required
                              />
                            </div>
                            <div className="ml-3 text-sm">
                              <label htmlFor="agreeDataProcessing" className="font-medium text-gray-700">
                                I consent to the processing of my company and personal data as described in the <a href="#" className="text-indigo-600 hover:text-indigo-500">Privacy Policy</a> <span className="text-red-500">*</span>
                              </label>
                            </div>
                          </div>
                        </div>
                        
                        <div className="pt-4">
                          <div className="flex justify-between">
                            <button
                              type="button"
                              onClick={prevStep}
                              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                            >
                              Back
                            </button>
                            <button
                              type="submit"
                              disabled={!isStepValid(4) || isLoading}
                              className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white ${
                                isStepValid(4) && !isLoading ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-indigo-300 cursor-not-allowed'
                              }`}
                            >
                              {isLoading ? (
                                <>
                                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                  </svg>
                                  Processing...
                                </>
                              ) : (
                                'Complete Registration'
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </form>
              </div>
            </>
          )}
        </div>
      </div>
      
      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 mt-auto">
        <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center justify-between md:flex-row">
            <div className="flex items-center">
              <svg className="h-6 w-6 text-indigo-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 5v14M5 12h14"/>
              </svg>
              <span className="ml-2 text-sm text-gray-500">© 2025 Business Central. All rights reserved.</span>
            </div>
            <div className="mt-4 md:mt-0">
              <div className="flex space-x-6">
                <a href="#" className="text-sm text-gray-500 hover:text-gray-900">Privacy Policy</a>
                <a href="#" className="text-sm text-gray-500 hover:text-gray-900">Terms of Service</a>
                <a href="#" className="text-sm text-gray-500 hover:text-gray-900">Help Center</a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default EnhancedRegistration;